import { buildOptions } from "./difficulty";
import type { CaseExercise, CaseStep, Difficulty, LearningExercise, LearningState } from "../types";

export function caseStepId(caseId: string, index: number): string {
  return `${caseId}#step-${index + 1}`;
}

// Assembliert die anzuzeigenden Optionen eines Schritts fuer eine Schwierigkeitsstufe
// (richtige Antwort + 3 Distraktoren der Stufe), deterministisch ueber den Seed.
export function assembleStep(
  step: CaseStep,
  difficulty: Difficulty,
  seed: number
): { options: string[]; answers: number[] } {
  return buildOptions(step.correct, step.distractors[difficulty], seed);
}

// Adapter: projiziert einen Subsumtions-Schritt (mit bereits assemblierten Optionen) auf ein
// LearningExercise, damit die bestehende SR-Engine (recordAttempt/scoreExercise) ihn verarbeitet.
export function stepAsExercise(
  caseEx: CaseExercise,
  index: number,
  assembled: { options: string[]; answers: number[] }
): LearningExercise {
  const step = caseEx.steps[index];
  return {
    id: caseStepId(caseEx.id, index),
    pathId: caseEx.pathId,
    mode: "case",
    kind: assembled.answers.length > 1 ? "multi" : "single",
    topic: caseEx.topic,
    title: step.question,
    prompt: caseEx.sachverhalt,
    options: assembled.options,
    answers: assembled.answers,
    explanation: step.explanation,
    sourceIds: step.sources.map((s) => s.id),
    sources: step.sources,
    level: caseEx.level,
    levelNumber: caseEx.levelNumber,
    errorCategory: "Subsumtion",
    skillTags: caseEx.skillTags,
    reviewStatus: caseEx.reviewStatus,
  };
}

export function evaluateStep(answers: number[], selected: number[]): { correct: boolean } {
  const a = [...answers].sort((x, y) => x - y);
  const b = [...selected].sort((x, y) => x - y);
  return { correct: a.length === b.length && a.every((v, i) => v === b[i]) };
}

export type StepStatus = "done" | "current" | "upcoming";

// Erster noch nicht beantworteter Schritt = "current"; bereits beantwortete = "done".
export function caseStepStates(state: LearningState, caseEx: CaseExercise): Array<{ status: StepStatus }> {
  let currentAssigned = false;
  return caseEx.steps.map((_, index) => {
    const card = state.cards[caseStepId(caseEx.id, index)];
    const answered = Boolean(card) && card.correct + card.wrong > 0;
    if (answered) return { status: "done" as StepStatus };
    if (!currentAssigned) {
      currentAssigned = true;
      return { status: "current" as StepStatus };
    }
    return { status: "upcoming" as StepStatus };
  });
}
